import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { EMPTY, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DateUtils } from 'src/app/@core/utils/date-utils';
import { Paging } from 'src/app/models/page';
import { ResponseModel } from 'src/app/models/response-model';
import { environment } from 'src/environments/environment';
import { OnBaseCatalogueType, Personality } from '../@core/constants';
import { FileUtils } from '../@core/utils/file-utils';
import { CreditRequest } from '../models/credit-request';
import { CreditRequestData } from '../models/credit-request-data';
import { Customer } from '../models/customer';
import { FileType } from '../models/file-type';
import { Filter } from '../models/filter';
import { OnbaseRequestFilter } from '../models/filters/onbase-request-filter';
import { OnbaseCatalogue } from '../models/onbase-catalogue';
import { OnbaseDoc } from '../models/onbase-doc';
import { Param } from '../models/param';
import { ImportRequirementDocumentRequest } from '../models/request/import-requirement-document-request';
import { OnBaseDraftDocumentsRequestModel } from '../models/response/onbase-draft-document-model';
import { OnbaseTimesResponseModel } from '../models/response/onbase-times-response-model';
import { OnbaseProcedureResponseModel } from './../models/response/onbaseProcedureResponseModel';
import { ConfigurationService } from './configuration.service';

@Injectable({
  providedIn: 'root',
})
export class OnbaseService {
  private urlOnBaseCatalogue: string = `${environment.url}/onbaseCatalogue`;
  private urlOnBaseProcedure: string = `${environment.url}/onbaseProcedures`;
  private urlOnBaseRequest: string = `${environment.url}/onbaserequests`;
  private urlOnBaseDocs: string = `${environment.url}/onbaseDocs`;
  constructor(
    private http: HttpClient,
    private configService: ConfigurationService
  ) { }

  readOnBaseCatalogueObject(
    catalogType: OnBaseCatalogueType,
    personality?: Personality | undefined
  ): Observable<Param[]> {
    let params = new HttpParams();

    params = personality ? params.set(`personality`, personality) : params;

    return this.http
      .get<ResponseModel<Param[]>>(
        `${this.urlOnBaseCatalogue}/${catalogType}`,
        {
          params,
        }
      )
      .pipe(map((res: ResponseModel<Param[]>) => res.response));
  }

  readOnBaseCatalogue(): Observable<ResponseModel<OnbaseCatalogue>> {
    return this.http.get<ResponseModel<OnbaseCatalogue>>(
      `${this.urlOnBaseCatalogue}`
    );
  }

  readPreApproveCreditPlans(
    customerCode: string,
    branchCode: string,
    limitRule: string,
    salesOrg: string,
    productCode: string,
    sellerCode: string
  ): Observable<ResponseModel<Paging<Array<OnbaseProcedureResponseModel>>>> {
    return this.http
      .get<ResponseModel<Paging<Array<OnbaseProcedureResponseModel>>>>(
        `${this.urlOnBaseProcedure}/${customerCode}`,
        {
          params: new HttpParams()
            .append(`branchCode`, branchCode)
            .append(`limitRule`, limitRule)
            .append(`salesOrg`, salesOrg)
            .append(`productCode`, productCode)
            .append(`sellerCode`, sellerCode),
        }
      )
      .pipe(
        map(
          (
            response: ResponseModel<Paging<Array<OnbaseProcedureResponseModel>>>
          ) => {
            response.response.content.forEach((op) => {
              op.writtenDuration = DateUtils.parseMinutesToDays(op.duration);
            });

            return response;
          }
        )
      );
  }

  getRequests(
    customerCode: string,
    inProcess?: boolean,
    text?: string,
    page?: number,
    pageSize?: number,
    salesOrg?: string,
    branch?: string
  ): Observable<ResponseModel<Paging<Array<CreditRequest>>>> {
    let params = new HttpParams();

    params = params.appendAll({
      customerCode,
      page: String(page ?? '0'),
      pageSize: String(pageSize ?? '100'),
      orderBy: 'REQUESTED',
      ascending: 'false',
    });

    params = inProcess ? params.append('inProcess', 'true') : params;
    return this.http.get<ResponseModel<Paging<Array<CreditRequest>>>>(
      `${environment.url}/onbaserequests`,
      { params }
    );
  }

  readOnbaseRequestByCode(
    code: string
  ): Observable<ResponseModel<CreditRequest>> {
    return this.http.get<ResponseModel<CreditRequest>>(
      `${this.urlOnBaseRequest}/${code}`
    );
  }

  readOnbaseRequestByCustomerCode(
    customerCode: string
  ): Observable<ResponseModel<Paging<Array<CreditRequest>>>> {
    return this.http.get<ResponseModel<Paging<Array<CreditRequest>>>>(
      this.urlOnBaseRequest,
      {
        params: new HttpParams().append('customerCode', customerCode),
      }
    );
  }

  readOnbaseRequestIntermediariesByCustomerCode(
    customerCode: string
  ): Observable<ResponseModel<Customer[]>> {
    const url = `${this.urlOnBaseRequest}/${customerCode}/intermediaries`;
    return this.http.get<ResponseModel<Customer[]>>(url);
  }

  readOnbaseRequestByFilters(
    filters: Filter<OnbaseRequestFilter>
  ): Observable<ResponseModel<Paging<Array<CreditRequest>>>> {
    let params = new HttpParams();

    params = params
      .set('page', filters.page.toString())
      .set('pageSize', filters.pageSize.toString());

    Object.keys(filters.value).forEach((k) => {
      if (filters.value[k] !== undefined && filters.value[k] !== ``) {
        params = params.append(k, filters.value[k]!);
      }
    });

    return this.http.get<ResponseModel<Paging<Array<CreditRequest>>>>(
      this.urlOnBaseRequest,
      { params }
    );
  }

  readCreditPlans(): Observable<
    ResponseModel<Paging<Array<OnbaseProcedureResponseModel>>>
  > {
    return this.http.get<
      ResponseModel<Paging<Array<OnbaseProcedureResponseModel>>>
    >(this.urlOnBaseProcedure);
  }

  getServerDateTime(): Observable<ResponseModel<string>> {
    const url = `${environment.url}/onbaserequests/serverDateTime`;
    return this.http.get<ResponseModel<string>>(url);
  }

  readOnBaseDocs(): Observable<ResponseModel<Array<OnbaseDoc>>> {
    return this.http.get<ResponseModel<Array<OnbaseDoc>>>(this.urlOnBaseDocs);
  }

  createOnBaseRequest(request: CreditRequestData, isDraft: boolean, biometryLogCode?: string) {
    let params: HttpParams = new HttpParams();

    params = params
      .set('draft', String(isDraft))
      .set('isFranchised', 'false')
      .set(
        'branchCode',
        this.configService.readSelectedBranchAndSalesOrg().branch.code
      )
      .set(
        'salesOrgCode',
        this.configService.readSelectedBranchAndSalesOrg().salesOrg.code
      )
      .set("systemOrigin", "salesforce");

    params = biometryLogCode ? params.append("biometryLogCode", biometryLogCode) : params;

    return this.http.post<ResponseModel<CreditRequest>>(
      this.urlOnBaseRequest,
      request,
      { params: params }
    );
  }
  sendDocuments(
    requestCode: string,
    fiscalId: string,
    documentForm: FormArray
  ): Promise<void> {
    this.http.delete(`${environment.url}/deleteDocuments/${requestCode}`).toPromise();
    return new Promise(async (resolve, reject) => {
      documentForm.controls.forEach((f) => {
        let documents = f.get('documents') as FormArray;

        documents.controls.forEach(async (q, i) => {
          await this.sendDocumentFromFormGroup(
            q as FormGroup,
            requestCode,
            fiscalId,
            f.value.id,
            i.toString()
          ).catch((error) => {
            reject(error);
          });
        });
        resolve();
      });
    });
  }

  async sendDocumentFromFormGroup(
    form: FormGroup,
    requestCode: string,
    fiscalId: string,
    documentType: string,
    i: string
  ) {
    await (
      await this.sendDocument(
        form.get(`document_${documentType}_${i}`)?.value as File,
        form.get(`document_ext_${documentType}_${i}`)?.value,
        requestCode,
        fiscalId,
        form.get(`document_documentType_${documentType}_${i}`)?.value,
        documentType
      )
    ).toPromise();
  }

  async sendDocument(
    file: File,
    extension: string,
    requestCode: string,
    fiscalId: string,
    fileType: string,
    documentType: string
  ) {
    if (file) {
      const body = {
        file: (await FileUtils.getBase64(file)).split('base64,')[1],
        extention: extension,
        identification: fiscalId,
        numSolicitud: requestCode,
        tipoArchivo: fileType,
        tipoDocumental: documentType,
      };

      return this.http.post<ResponseModel<string>>(
        `${this.urlOnBaseRequest}/documents/import`,
        body
      );
    }

    return EMPTY;
  }

  updateOnBaseRequest(
    requestCode: string,
    request: CreditRequestData,
    isDraft: boolean,
    biometryLogCode?: string
  ) {
    let params: HttpParams = new HttpParams();

    params = params
      .set('draft', String(isDraft))
      .set('isFranchised', 'false')
      .set(
        'branchCode',
        this.configService.readSelectedBranchAndSalesOrg().branch.code
      )
      .set(
        'salesOrgCode',
        this.configService.readSelectedBranchAndSalesOrg().salesOrg.code
      )
      .set("systemOrigin", "salesforce");

    params = biometryLogCode ? params.append("biometryLogCode", biometryLogCode) : params;

    return this.http.put<ResponseModel<CreditRequest>>(
      `${this.urlOnBaseRequest}/${requestCode}`,
      request,
      { params: params }
    );
  }

  updateBankGuarantee(requestCode: string, bankGuaranteeRequestCode: string) {
    let params: HttpParams = new HttpParams();

    params = params.set('avalCode', bankGuaranteeRequestCode);

    return this.http.post<ResponseModel<CreditRequest>>(
      `${this.urlOnBaseRequest}/${requestCode}/updateAval`,
      {},
      { params }
    );
  }

  async sendDocumentsDraft(
    requestCode: string,
    fiscalId: string,
    form: FormArray
  ) {
    let body = {
      documents: (
        await Promise.all(
          form.value.map(async (m: any) => {
            let docArray = m.documents as Array<any>;
            let result = await Promise.all(
              docArray.map(async (x: any, i: number) => {
                let file = x[`document_${m.id}_${i}`];
                if (file) {
                  return {
                    documentType: m.id,
                    fileType: x[`document_documentType_${m.id}_${i}`],
                    requestNumber: requestCode,
                    identificationDocument: fiscalId,
                    file: await FileUtils.getBase64(file),
                    category: m.description,
                    documentList: x[`description_${m.id}_${i}`],
                    extentionFile: x[`document_ext_${m.id}_${i}`],
                    id: `${i.toString()}`,
                    toView: true,
                    fileName: x[`document_fileName_${m.id}_${i}`],
                  };
                }
                return;
              })
            );

            return result;
          }))).flatMap(f => f).filter(f => f)
    }
    console.log(body)

    if (body.documents.length == 0) {

      return this.http.delete(`${environment.url}/deleteDocuments/${requestCode}`);
    }
    return this.http.post(`${environment.url}/saveDocuments`, body);
  }

  getFileType(fileName: string) {
    let fileTypes: FileType[] = [
      { type: 'image', code: '2', ext: 'jpg' },
      { type: 'image', code: '2', ext: 'png' },
      { type: 'image', code: '2', ext: 'jpeg' },
      { type: 'word', code: '12', ext: 'doc' },
      { type: 'word', code: '12', ext: 'docx' },
      { type: 'excel', code: '13', ext: 'xls' },
      { type: 'excel', code: '13', ext: 'csv' },
      { type: 'excel', code: '13', ext: 'xlsx' },
      { type: 'pdf', code: '16', ext: 'pdf' },
    ];
    let ext = fileName.trim().split('.').pop()?.toLowerCase();
    return fileTypes.find((f) => f.ext == ext) || new FileType();
  }

  listFilesDraft(requestCode: string) {
    return this.http.get<ResponseModel<OnBaseDraftDocumentsRequestModel>>(
      `${environment.url}/getDocuments/${requestCode}`
    );
  }

  getTimerSellerAndCustomer(
    requestCode: string,
    requestType: string
  ): Observable<ResponseModel<OnbaseTimesResponseModel>> {
    const params = new HttpParams()
      .set('requestCode', requestCode)
      .set('requestType', requestType);

    return this.http.get<ResponseModel<OnbaseTimesResponseModel>>(
      `${this.urlOnBaseRequest}/onBaseTimes`,
      { params }
    );
  }

  confirmRequirement(
    creditRequestNumber: string,
    comments: string,
    requirementNumber: string
  ): Observable<any> {
    return this.http.post(`${this.urlOnBaseRequest}/requirements/confirm`, {
      notes: comments,
      requestObjectId: creditRequestNumber,
      requirementObjectId: requirementNumber,
    });
  }

  importDocumentForRequirement(
    importRequirementDocumentRequest: ImportRequirementDocumentRequest
  ): Observable<any> {
    return this.http.post(
      `${this.urlOnBaseRequest}/documents/import`,
      importRequirementDocumentRequest
    );
  }
}
